import React, { useEffect } from "react";
import Icon from "./icon";
import { Button, Box, Flex } from "theme-ui";
import Container from "./container";
import ThemedLink from "./atoms/ThemedLink";
import { alpha } from "@theme-ui/color";
import StatusPill from "./status-pill";

const Branding = (props) => (
  <Box flex="1">
    <ThemedLink
      to="/"
      variant="navBorderless"
      sx={{ pl: 0 }}
      aria-label="Return to Home"
      activeClassName="active"
    >
      <Icon
        symbol="logo"
        sx={{
          verticalAlign: "bottom",
          display: "inline-block",
        }}
      />
      <span sx={{ fontSize: "20px", pl: 1, display: ["inherit", "none", "inherit"] }}>Lalaine</span>
    </ThemedLink>
  </Box>
);

const ToggleButton = ({ showNav, onHideNav, onShowNav }) => (
  <Button
    onClick={showNav ? onHideNav : onShowNav}
    fontSize={4}
    m={0}
    p={2}
    aria-label={showNav ? "Close menu" : "Open menu"}
    aria-expanded={showNav}
    aria-controls="mobile-navigation"
    sx={{
      display: ["block", "none"],
      border: "none",
      color: "inherit",
      background: "none",
      zIndex: 101,
    }}
  >
    <Icon symbol={showNav ? "close" : "hamburger"} sx={{ height: 28, width: 28 }} />
  </Button>
);

const NavLink = ({ to, children, onClick }) => (
  <ThemedLink
    to={to}
    onClick={onClick}
    variant="nav"
    sx={{
      display: "block",
      fontSize: [8, 2, 3],
      mx: [2, 1, 2],
      my: [0, 1, 1],
      py: [4, 1, 1],
      px: [2, 1, 1],
    }}
    activeClassName="active"
  >
    {children}
  </ThemedLink>
);

const Nav = ({ showNav, onHideNav, onShowNav, status }) => (
  <Box
    as="nav"
    id="mobile-navigation"
    color="text"
    sx={{
      display: [showNav ? "flex" : "none", "flex"],
      position: ["fixed", "static"],
      inset: [0, "auto"],
      width: ["100%", "auto"],
      height: ["100dvh", "auto"],
      overflowY: ["auto", "visible"],
      overscrollBehavior: ["contain", "auto"],
      pt: 2,
      backgroundColor: "background",
      zIndex: [100, "auto"],
      justifyContent: "center",
    }}
  >
    <Flex
      as="ul"
      sx={{
        listStyle: "none",
        justifyContent: "center",
        marginX: "auto",
        flexShrink: 0,
        p: [2, 0],
        flexDirection: ["column", "row"],
        alignItems: ["center", "inherit"],
      }}
    >
      <li>
        <NavLink to="/about/" onClick={onHideNav}>
          About
        </NavLink>
      </li>
      <li>
        <NavLink to="/projects/" onClick={onHideNav}>
          Projects
        </NavLink>
      </li>
      <li>
        <NavLink to="/contact/" onClick={onHideNav}>
          Contact
        </NavLink>
      </li>
    </Flex>
  </Box>
);

const Header = ({ onHideNav, onShowNav, showNav, siteTitle, status }) => {
  useEffect(() => {
    if (!showNav) return;

    const html = document.documentElement;
    const body = document.body;

    html.classList.add("nav-open");
    body.classList.add("nav-open");

    // Close the navigation with Escape
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onHideNav();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      html.classList.remove("nav-open");
      body.classList.remove("nav-open");
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showNav, onHideNav]);

  return (
    <Flex
      sx={{
        position: "sticky",
        top: 0,
        width: "100%",
        bg: "background",
        alignItems: "center",
        zIndex: "99999",
      }}
      as="header"
    >
      <Container
        px={[2]}
        py={[1, 2]}
        sx={{
          position: "relative",
          display: "flex",
          flex: 1,
          justifyContent: "space-between",
          "::before": {
            content: '""',
            display: "block",
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "1.5px",
            backgroundImage: (t) => `
            linear-gradient(
              to right,
              ${alpha("primary", 1)(t)},
              ${alpha("background", 1)(t)}
            )
          `,
            borderRadius: "default",
          },
        }}
      >
        <Box
          sx={{
            display: ["flex", "flex", "block"],
            flex: [1],
            justifyContent: "space-between",
          }}
        >
          <Branding />
          <ToggleButton showNav={showNav} onHideNav={onHideNav} onShowNav={onShowNav} />
        </Box>
        <Nav showNav={showNav} onHideNav={onHideNav} onShowNav={onShowNav} status={status} />
      </Container>
    </Flex>
  );
};

export default Header;
